import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import SwitzerlandMeetupApp from './switzerland-meetup-app';
import BetaSignup from './BetaSignup';
import AdminDashboard from './AdminDashboard';
import AdminLogin from './AdminLogin';
import ConceptMatching from './ConceptMatching';
import ConceptDirectory from './ConceptDirectory';
import ConceptEvents from './ConceptEvents';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase/config';
import { isAdmin } from './firebase/admin';

// Protected App Route Component
const ProtectedApp = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Check if user is admin
  if (!currentUser || !isAdmin(currentUser)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="bg-red-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Access Restricted</h1>
          <p className="text-gray-600 mb-4">
            This app is currently in closed beta testing.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Only authorized administrators can access this area. Please sign in with an admin account or join our beta waitlist.
          </p>
          <div className="space-y-3">
            <a
              href="/"
              className="block px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
            >
              Join Beta Waitlist
            </a>
            <a
              href="/admin-login"
              className="block px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              Admin Login
            </a>
          </div>
        </div>
      </div>
    );
  }

  // User is admin - show the main app
  return <SwitzerlandMeetupApp />;
};

// Simple router based on URL path
const App = () => {
  const path = window.location.pathname;
  
  // Admin login page
  if (path === '/admin-login') {
    return <AdminLogin />;
  }
  
  // Admin dashboard - always accessible
  if (path === '/admin') {
    return <AdminDashboard />;
  }
  
  // Protected main app - requires admin login
  if (path === '/app43284832') {
    return <ProtectedApp />;
  }
  
  // Concept demo pages - publicly accessible for validation
  if (path === '/concept-matching') {
    return <ConceptMatching />;
  }
  
  if (path === '/concept-directory') {
    return <ConceptDirectory />;
  }
  
  if (path === '/concept-events') {
    return <ConceptEvents />;
  }
  
  // Everything else (including root /) shows beta signup
  return <BetaSignup />;
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);